import PropTypes from 'prop-types';
import React, { useRef, useEffect } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, useIntl } from 'react-intl';

import { PrimarySolidButton, IconButton } from '@bibliocommons/base-buttons';
import { Close } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import FormattedHTMLMessage from 'app/components/shared/FormattedHTMLMessage';
import Heading from 'app/components/shared/Heading';
import FeedbackContactLinks from './FeedbackContactLinks';
import FeedbackForm from './FeedbackForm';

import './FeedbackBox.scss';

export function FeedbackBox({
  categories,
  contactLinks,
  selectedCategory,
  isSubmitting,
  expanded,
  actions,
  suggestForPurchaseEnabled
}) {
  const intl = useIntl();
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (expanded) {
      setTimeout(() => dropdownRef.current?.querySelector('button')?.focus(), 0);
    }
  }, [expanded]);

  const renderCloseButton = visibleOnMobile => (
    <IconButton
      dataKey="feedback-close-button"
      className={`feedback-close-button ${visibleOnMobile ? 'visible-xs' : 'hidden-xs'}`}
      aria-label={intl.formatMessage({ id: 'button_close' })}
      handleClick={actions.collapseFeedbackBox}
    >
      <Close />
      <ScreenReaderMessage>
        <FormattedMessage id="button_close" />
      </ScreenReaderMessage>
    </IconButton>
  );

  const renderCollapsed = () => (
    <div className="feedback-teaser container clearfix">
      <div className="feedback-left-content">
        <Heading tagName="h3" size="medium" className="category-name">
          <FormattedMessage id="feedback_heading" />
        </Heading>
        <FormattedMessage tagName="p" id="feedback_prompt" />
      </div>

      <div className="feedback-right-content ">
        <PrimarySolidButton
          className="feedback-expand-btn"
          dataKey="submit-feedback-button"
          handleClick={actions.expandFeedbackBox}
        >
          <FormattedMessage id="feedback_submit_feedback_btn" />
        </PrimarySolidButton>
      </div>
    </div>
  );

  const renderExpanded = () => (
    <div className="feedback-expanded container clearfix">
      <div className="feedback-left-content">
        <Heading tagName="h3" size="medium">
          <FormattedMessage id="feedback_heading" />
        </Heading>
        <FormattedHTMLMessage tagName="p" id="feedback_review_notice" />

        <div ref={dropdownRef}>
          <FeedbackForm
            categories={categories}
            selectedCategory={selectedCategory}
            isSubmitting={isSubmitting}
            actions={actions}
            suggestForPurchaseEnabled={suggestForPurchaseEnabled}
          />
        </div>
      </div>

      <div className="feedback-right-content">
        {renderCloseButton(true)}
        {renderCloseButton(false)}
        <div className="contact-links">
          <FeedbackContactLinks contactLinks={contactLinks} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="cp-feedback-box">
      <div className="feedback-wrapper">{expanded ? renderExpanded() : renderCollapsed()}</div>
    </div>
  );
}

FeedbackBox.propTypes = {
  categories: ImmutablePropTypes.listOf(PropTypes.string),
  contactLinks: ImmutablePropTypes.mapOf(PropTypes.string, PropTypes.string),
  selectedCategory: PropTypes.string,
  isSubmitting: PropTypes.bool,
  expanded: PropTypes.bool,
  actions: PropTypes.objectOf(PropTypes.func),
  suggestForPurchaseEnabled: PropTypes.bool
};
